import { TableOptions } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { Stack } from 'src/core/ui/Stack';
import {
  getSimplifiedUndercutMode,
  SimplifiedUndercutMode,
} from 'src/utils/autoPricingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { UNDERCUT_REFERENCE_LISTING_POSITION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getAllInPriceFromListPrice } from 'src/utils/inventoryUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { hasChanged } from 'src/utils/posFieldUtils';
import {
  ListingDetailsAutoPricingSectionUpdatesV2,
  Marketplace,
  SectionFilterMode
} from 'src/WebApiController';

import {
  AutoPricingSettingsInput,
  PricingSettingsUpdateKey,
} from '../BulkEditAutoPricingSettingsDialog.types';
import { PricingSettingsPreviewColumns } from './PricingSettingsPreview.constants';
import { PreviewColumn } from './PricingSettingsPreview.types';

const booleanToText = (
  value: boolean | null | undefined,
  defaultText: string,
  reverse = false
) => {
  if (value == null) return defaultText;
  return Boolean(reverse ? !value : value) + '';
};

export const usePricingSettingsPreviewTable = (
  settingsInput: AutoPricingSettingsInput,
  defaultValues: ListingDetailsAutoPricingSectionUpdatesV2,
  updates: ListingDetailsAutoPricingSectionUpdatesV2,
  updateChangedFields: (
    values: Partial<Record<PricingSettingsUpdateKey, boolean | undefined>>
  ) => void,
  changedFields?: Partial<Record<PricingSettingsUpdateKey, boolean | undefined>>
): Partial<TableOptions<PreviewColumn>> => {
  const conflictText = useContent(ContentId.Conflict);
  const existingSectionsText = useContent(ContentId.ExistingSections);
  const overrideText = useContent(ContentId.Override);
  const naText = useContent(ContentId.NA);
  const sameZoneOrBetter = useContent(ContentId.SameZoneOrBetter);
  const sameZone = useContent(ContentId.SameZone);
  const useAccountDefaultText = useContent(ContentId.UseAccountDefault);
  const mySectionText = useContent(ContentId.MySection);
  const rowCompsText = useContent(ContentId.RowComps);

  const { loginContext } = useAppContext();
  const sellerFee =
    loginContext?.user?.activeAccount?.marketplaceSettings?.find(
      (m) => m.mkp === Marketplace.StubHub
    )?.sellerFee;

  const { venueMapInfo } = useEventMapContext();

  const sectionIdToName = useMemo(() => {
    return (venueMapInfo?.sections ?? []).reduce(
      (res, section) => {
        res[section.id] = section.name;
        return res;
      },
      {} as Record<number, string>
    );
  }, [venueMapInfo?.sections]);

  const getSectionsValueText = useCallback(
    (values: ListingDetailsAutoPricingSectionUpdatesV2) => {
      let sectionsValueText = '';
      if (
        values?.compListingSelectedSectionSettings?.value?.sectionFilterMode ==
        SectionFilterMode.SameSection
      ) {
        sectionsValueText = mySectionText;
      } else if (values.deriveCompsFromVenueZoneConfig) {
        const venueMapName = values.venueZoneMapName;
        const betterZonesValue = values.includeBetterZones
          ? sameZoneOrBetter
          : sameZone;
        sectionsValueText = `${venueMapName}, ${betterZonesValue}`;
      } else {
        const updatedSectionNames =
          values?.compListingSelectedSectionSettings?.value?.sectionIdFilter?.map(
            (id) => sectionIdToName[id]
          ) ?? [];
        sectionsValueText = `${
          values.includeExistingSelectedSections
            ? existingSectionsText + ', '
            : overrideText
        } ${updatedSectionNames.join(',')}`;
      }

      const rowOffset =
        values?.compListingSelectedSectionSettings?.value?.rowOffset;
      if (rowOffset && rowOffset >= 0) {
        sectionsValueText += `, ${rowCompsText}: ${rowOffset}`;
      }

      return sectionsValueText;
    },
    [
      existingSectionsText,
      mySectionText,
      overrideText,
      rowCompsText,
      sameZone,
      sameZoneOrBetter,
      sectionIdToName,
    ]
  );

  const data = useMemo((): PreviewColumn[] => {
    const settings: PreviewColumn[] = [];
    if (
      hasChanged(updates?.autoPricingEnabled, defaultValues.autoPricingEnabled)
    ) {
      settings.push({
        setting: ContentId.EnableAutoPricing,
        from: settingsInput.autoPricingEnabled?.hasConflict
          ? conflictText
          : defaultValues.autoPricingEnabled?.value + '',
        to: updates.autoPricingEnabled?.value + '',
        include: !!changedFields?.autoPricingEnabled,
        onChange: (include: boolean) => {
          updateChangedFields({
            autoPricingEnabled: include,
          });
        },
      });
    }

    if (hasChanged(updates?.compListingMode, defaultValues.compListingMode)) {
      settings.push({
        setting: ContentId.AllListings,
        from: settingsInput.compListingMode?.hasConflict
          ? conflictText
          : defaultValues.compListingMode?.value,
        to: updates.compListingMode?.value,
        include: !!changedFields?.compListingMode,
        onChange: (include: boolean) => {
          updateChangedFields({
            compListingMode: include,
          });
        },
      });
    }

    if (
      hasChanged(
        updates?.compListingExcludeFanInventory,
        defaultValues.compListingExcludeFanInventory
      )
    ) {
      settings.push({
        setting: ContentId.IncludeFanInventory,
        from: settingsInput.compListingExcludeFanInventory?.hasConflict
          ? conflictText
          : booleanToText(
              !defaultValues.compListingExcludeFanInventory?.value,
              naText
            ),
        to: booleanToText(
          !updates.compListingExcludeFanInventory?.value,
          naText
        ),
        include: !!changedFields?.compListingExcludeFanInventory,
        onChange: (include: boolean) => {
          updateChangedFields({
            compListingExcludeFanInventory: include,
          });
        },
      });
    }
    if (
      hasChanged(
        updates?.compListingExcludeDefects,
        defaultValues.compListingExcludeDefects
      )
    ) {
      settings.push({
        setting: ContentId.IncludeDefects,
        from: settingsInput.compListingExcludeDefects?.hasConflict
          ? conflictText
          : booleanToText(
              defaultValues.compListingExcludeDefects?.value,
              naText,
              true
            ),
        to: booleanToText(
          updates.compListingExcludeDefects?.value,
          naText,
          true
        ),
        include: !!changedFields?.compListingExcludeDefects,
        onChange: (include: boolean) => {
          updateChangedFields({
            compListingExcludeDefects: include,
          });
        },
      });
    }

    if (
      hasChanged(
        updates?.compListingSelectedSectionSettings,
        defaultValues.compListingSelectedSectionSettings
      ) ||
      (settingsInput.compListingSelectedSectionSettings?.hasConflict &&
        !updates.includeExistingSelectedSections) ||
      updates.deriveCompsFromVenueZoneConfig !=
        defaultValues?.deriveCompsFromVenueZoneConfig ||
      updates.includeBetterZones != defaultValues?.includeBetterZones ||
      updates.rowCompOffset != defaultValues?.rowCompOffset
    ) {
      settings.push({
        setting: ContentId.Sections,
        from: settingsInput.compListingSelectedSectionSettings?.hasConflict
          ? conflictText
          : getSectionsValueText(defaultValues),
        to: getSectionsValueText(updates),
        include:
          !!changedFields?.compListingSelectedSectionSettings ||
          !!changedFields?.deriveCompsFromVenueZoneConfig ||
          !!changedFields?.includeBetterZones ||
          !!changedFields?.rowCompOffset,
        onChange: (include: boolean) => {
          updateChangedFields({
            compListingSelectedSectionSettings: include,
            deriveCompsFromVenueZoneConfig: include,
            includeBetterZones: include,
            rowCompOffset: include,
          });
        },
      });
    }

    if (
      hasChanged(
        updates?.compListingQuantityScoreAdjustmentOverrideJson,
        defaultValues.compListingQuantityScoreAdjustmentOverrideJson
      ) ||
      (settingsInput.compListingQuantityScoreAdjustmentOverrideJson
        ?.hasConflict &&
        updates?.useDefaultQuantityFilters)
    ) {
      settings.push({
        setting: ContentId.QuantitiesFilter,
        from: settingsInput.compListingQuantityScoreAdjustmentOverrideJson
          ?.hasConflict
          ? conflictText
          : defaultValues.compListingQuantityScoreAdjustmentOverrideJson
              ?.value + '',
        to:
          settingsInput.compListingQuantityScoreAdjustmentOverrideJson
            ?.hasConflict && updates?.useDefaultQuantityFilters
            ? useAccountDefaultText
            : updates.compListingQuantityScoreAdjustmentOverrideJson?.value +
              '',
        include:
          !!changedFields?.compListingQuantityScoreAdjustmentOverrideJson ||
          !!(
            settingsInput.compListingQuantityScoreAdjustmentOverrideJson
              ?.hasConflict && updates?.useDefaultQuantityFilters
          ),
        onChange: (include: boolean) => {
          updateChangedFields({
            compListingQuantityScoreAdjustmentOverrideJson: include,
          });
        },
      });
    }
    if (
      hasChanged(
        updates?.undercutAbsoluteAmount,
        defaultValues.undercutAbsoluteAmount
      ) ||
      hasChanged(
        updates?.undercutRelativeAmount,
        defaultValues.undercutRelativeAmount
      )
    ) {
      settings.push({
        setting: ContentId.UnderCutModePrompt,
        from:
          settingsInput.undercutAbsoluteAmount?.hasConflict ||
          settingsInput.undercutRelativeAmount?.hasConflict ? (
            conflictText
          ) : (
            <Stack direction="row" gap="s">
              <Content id={ContentId.PriceBy} />
              <div>
                {defaultValues.undercutRelativeAmount?.value
                  ? applyNumberFormatting({
                      inputNumber: defaultValues.undercutRelativeAmount.value,
                      isPercent: true,
                    })
                  : naText}
              </div>
              <Content id={ContentId.ThenBy} />
              <div>
                {defaultValues.undercutAbsoluteAmount?.value
                  ? applyNumberFormatting({
                      inputNumber: defaultValues.undercutAbsoluteAmount.value,
                      currencyCode: defaultValues.currencyCode,
                    })
                  : naText}
              </div>
            </Stack>
          ),
        to: (
          <Stack direction="row" gap="s">
            <Content id={ContentId.PriceBy} />
            <div>
              {updates.undercutRelativeAmount?.value
                ? applyNumberFormatting({
                    inputNumber: updates.undercutRelativeAmount.value,
                    isPercent: true,
                  })
                : naText}
            </div>
            <Content id={ContentId.ThenBy} />
            <div>
              {updates.undercutAbsoluteAmount?.value
                ? applyNumberFormatting({
                    inputNumber: updates.undercutAbsoluteAmount.value,
                    currencyCode: updates.currencyCode,
                  })
                : naText}
            </div>
          </Stack>
        ),
        include:
          !!changedFields?.undercutAbsoluteAmount ||
          !!changedFields?.undercutRelativeAmount,
        onChange: (include: boolean) => {
          updateChangedFields({
            undercutAbsoluteAmount: include,
            undercutRelativeAmount: include,
          });
        },
      });
    }
    if (
      hasChanged(updates?.netProceedsFloor, defaultValues.netProceedsFloor) ||
      hasChanged(updates?.netProceedsCeiling, defaultValues.netProceedsCeiling)
    ) {
      settings.push({
        setting: ContentId.PriceBelowFloor,
        from:
          settingsInput.netProceedsFloor?.hasConflict ||
          settingsInput.netProceedsCeiling?.hasConflict ? (
            conflictText
          ) : (
            <Stack direction="row" gap="s">
              <div>
                {defaultValues.netProceedsFloor?.value
                  ? applyNumberFormatting({
                      inputNumber: getAllInPriceFromListPrice(
                        defaultValues.netProceedsFloor.value,
                        null,
                        sellerFee
                      ),
                      currencyCode: defaultValues.currencyCode,
                    })
                  : naText}
              </div>
              <Content id={ContentId.OrAbove} />
              <div>
                {defaultValues.netProceedsCeiling?.value
                  ? applyNumberFormatting({
                      inputNumber: getAllInPriceFromListPrice(
                        defaultValues.netProceedsCeiling.value,
                        null,
                        sellerFee
                      ),
                      currencyCode: defaultValues.currencyCode,
                    })
                  : naText}
              </div>
            </Stack>
          ),
        to: (
          <Stack direction="row" gap="s">
            <div>
              {updates.netProceedsFloor?.value
                ? applyNumberFormatting({
                    inputNumber: getAllInPriceFromListPrice(
                      updates.netProceedsFloor.value,
                      null,
                      sellerFee
                    ),
                    currencyCode: updates.currencyCode,
                  })
                : naText}
            </div>
            <Content id={ContentId.OrAbove} />
            <div>
              {updates.netProceedsCeiling?.value
                ? applyNumberFormatting({
                    inputNumber: getAllInPriceFromListPrice(
                      updates.netProceedsCeiling.value,
                      null,
                      sellerFee
                    ),
                    currencyCode: updates.currencyCode,
                  })
                : naText}
            </div>
          </Stack>
        ),
        include:
          !!changedFields?.netProceedsFloor ||
          !!changedFields?.netProceedsCeiling,
        onChange: (include: boolean) => {
          updateChangedFields({
            netProceedsFloor: include,
            netProceedsCeiling: include,
          });
        },
      });
    }
    if (
      updates?.netProceedsFloorAdjustmentAmount != null &&
      updates?.netProceedsFloorAdjustmentRelative != null &&
      updates?.netProceedsFloorAdjustmentType != null
    ) {
      settings.push({
        setting: ContentId.AdjustFloor,
        from: naText,
        to: (
          <Stack direction="row" gap="s">
            <div>
              {updates.netProceedsFloorAdjustmentRelative
                ? applyNumberFormatting({
                    inputNumber: updates.netProceedsFloorAdjustmentAmount / 100,
                    isPercent: true,
                  })
                : applyNumberFormatting({
                    inputNumber: updates.netProceedsFloorAdjustmentAmount,
                    currencyCode: updates.currencyCode,
                  })}
            </div>
            <Content id={ContentId.RelativeTo} />
            <div>{updates.netProceedsFloorAdjustmentType}</div>
          </Stack>
        ),
        include: !!changedFields?.netProceedsFloorAdjustmentAmount,
        onChange: (include: boolean) => {
          updateChangedFields({
            netProceedsFloorAdjustmentAmount: include,
          });
        },
      });
    }
    if (
      hasChanged(
        updates?.circuitBreakerMinCompListingCount,
        defaultValues.circuitBreakerMinCompListingCount
      )
    ) {
      settings.push({
        setting: ContentId.MinListingsRequired,
        from: settingsInput.circuitBreakerMinCompListingCount?.hasConflict
          ? conflictText
          : defaultValues.circuitBreakerMinCompListingCount?.value,
        to: updates.circuitBreakerMinCompListingCount?.value,
        include: !!changedFields?.circuitBreakerMinCompListingCount,
        onChange: (include: boolean) => {
          updateChangedFields({
            circuitBreakerMinCompListingCount: include,
          });
        },
      });
    }
    if (hasChanged(updates?.outlierMode, defaultValues.outlierMode)) {
      settings.push({
        setting: ContentId.OutlierMode,
        from: settingsInput.outlierMode?.hasConflict
          ? conflictText
          : defaultValues.outlierMode?.value,
        to: updates.outlierMode?.value,
        include: !!changedFields?.outlierMode,
        onChange: (include: boolean) => {
          updateChangedFields({
            outlierMode: include,
          });
        },
      });
    }
    if (
      hasChanged(
        updates?.outlierStandardDeviations,
        defaultValues.outlierStandardDeviations
      )
    ) {
      settings.push({
        setting: ContentId.OutlierFiltering,
        from: settingsInput.outlierStandardDeviations?.hasConflict
          ? conflictText
          : defaultValues.outlierStandardDeviations?.value,
        to: updates.outlierStandardDeviations?.value,
        include: !!changedFields?.outlierStandardDeviations,
        onChange: (include: boolean) => {
          updateChangedFields({
            outlierStandardDeviations: include,
          });
        },
      });
    }

    if (
      hasChanged(
        updates?.outlierKthLowestLimit,
        defaultValues.outlierKthLowestLimit
      ) &&
      (hasChanged(
        updates?.outlierKthLowestLimitAbsoluteSpacing,
        defaultValues.outlierKthLowestLimitAbsoluteSpacing
      ) ||
        hasChanged(
          updates?.outlierKthLowestLimitRelativeSpacing,
          defaultValues.outlierKthLowestLimitRelativeSpacing
        ))
    ) {
      settings.push({
        setting: ContentId.IgnoreTheFirst,
        from:
          settingsInput.outlierKthLowestLimit?.hasConflict ||
          settingsInput.outlierKthLowestLimitAbsoluteSpacing?.hasConflict ||
          settingsInput.outlierKthLowestLimitRelativeSpacing?.hasConflict ? (
            conflictText
          ) : (
            <Stack direction="row" gap="s">
              <div>{defaultValues.outlierKthLowestLimit?.value ?? naText}</div>
              <Content id={ContentId.MoreThan} />
              <div>
                {defaultValues.outlierKthLowestLimitAbsoluteSpacing?.value
                  ? applyNumberFormatting({
                      inputNumber:
                        defaultValues.outlierKthLowestLimitAbsoluteSpacing
                          ?.value,
                    })
                  : defaultValues.outlierKthLowestLimitRelativeSpacing?.value
                    ? applyNumberFormatting({
                        inputNumber:
                          defaultValues.outlierKthLowestLimitRelativeSpacing
                            ?.value,
                        isPercent: true,
                      })
                    : naText}
              </div>
              <Content id={ContentId.TheNextCheapest} />
            </Stack>
          ),
        to: (
          <Stack direction="row" gap="s">
            <div>{updates.outlierKthLowestLimit?.value ?? naText}</div>
            <Content id={ContentId.MoreThan} />
            <div>
              {updates.outlierKthLowestLimitAbsoluteSpacing?.value
                ? applyNumberFormatting({
                    inputNumber:
                      updates.outlierKthLowestLimitAbsoluteSpacing?.value,
                  })
                : updates.outlierKthLowestLimitRelativeSpacing?.value
                  ? applyNumberFormatting({
                      inputNumber:
                        updates.outlierKthLowestLimitRelativeSpacing.value,
                      isPercent: true,
                    })
                  : naText}
            </div>
            <Content id={ContentId.TheNextCheapest} />
          </Stack>
        ),
        include:
          !!changedFields?.outlierKthLowestLimit ||
          !!changedFields?.outlierKthLowestLimitAbsoluteSpacing ||
          !!changedFields?.outlierKthLowestLimitRelativeSpacing,
        onChange: (include: boolean) => {
          updateChangedFields({
            outlierKthLowestLimit: include,
            outlierKthLowestLimitAbsoluteSpacing: include,
            outlierKthLowestLimitRelativeSpacing: include,
          });
        },
      });
    }

    if (
      hasChanged(
        updates?.circuitBreakerMaxDiscountVelocityPercent,
        defaultValues?.circuitBreakerMaxDiscountVelocityPercent
      ) ||
      hasChanged(
        updates?.circuitBreakerMaxDiscountVelocityTicksInHours,
        defaultValues?.circuitBreakerMaxDiscountVelocityTicksInHours
      )
    ) {
      settings.push({
        setting: ContentId.DonotDropMoreThan,
        from:
          settingsInput.circuitBreakerMaxDiscountVelocityPercent?.hasConflict ||
          settingsInput.circuitBreakerMaxDiscountVelocityPercent
            ?.hasConflict ? (
            conflictText
          ) : (
            <Stack direction="row" gap="s">
              <div>
                {defaultValues.circuitBreakerMaxDiscountVelocityPercent?.value
                  ? applyNumberFormatting({
                      inputNumber:
                        defaultValues.circuitBreakerMaxDiscountVelocityPercent
                          ?.value,
                      isPercent: true,
                    })
                  : naText}
              </div>
              <Content id={ContentId.MaxDiscountInputPrompt2} />
              <div>
                {defaultValues.circuitBreakerMaxDiscountVelocityTicksInHours
                  ?.value
                  ? applyNumberFormatting({
                      inputNumber:
                        defaultValues
                          .circuitBreakerMaxDiscountVelocityTicksInHours?.value,
                    })
                  : naText}
              </div>
              <Content id={ContentId.MaxDiscountInputPrompt3} />
            </Stack>
          ),
        to: (
          <Stack direction="row" gap="s">
            <div>
              {updates.circuitBreakerMaxDiscountVelocityPercent?.value
                ? applyNumberFormatting({
                    inputNumber:
                      updates.circuitBreakerMaxDiscountVelocityPercent.value,
                    isPercent: true,
                  })
                : naText}
            </div>
            <Content id={ContentId.MaxDiscountInputPrompt2} />
            <div>
              {updates.circuitBreakerMaxDiscountVelocityTicksInHours?.value
                ? applyNumberFormatting({
                    inputNumber:
                      updates.circuitBreakerMaxDiscountVelocityTicksInHours
                        ?.value,
                  })
                : naText}
            </div>
            <Content id={ContentId.MaxDiscountInputPrompt3} />
          </Stack>
        ),
        include:
          !!changedFields?.circuitBreakerMaxDiscountVelocityPercent ||
          !!changedFields?.circuitBreakerMaxDiscountVelocityTicksInHours,
        onChange: (include: boolean) => {
          updateChangedFields({
            circuitBreakerMaxDiscountVelocityPercent: include,
            circuitBreakerMaxDiscountVelocityTicksInHours: include,
          });
        },
      });
    }

    if (
      hasChanged(
        updates?.undercutReferenceListingPosition,
        defaultValues?.undercutReferenceListingPosition
      ) ||
      hasChanged(updates?.undercutMode, defaultValues?.undercutMode)
    ) {
      const oldReferencePosition =
        defaultValues?.undercutReferenceListingPosition?.value;
      const oldSimplifiedUndercutMode = getSimplifiedUndercutMode(
        defaultValues?.undercutMode?.value,
        oldReferencePosition
      );

      const newReferencePosition =
        updates?.undercutReferenceListingPosition?.value;
      const newSimplifiedUndercutMode = getSimplifiedUndercutMode(
        updates?.undercutMode?.value,
        newReferencePosition
      );

      settings.push({
        setting: ContentId.Undercut,
        from:
          settingsInput.undercutMode?.hasConflict ||
          settingsInput.undercutReferenceListingPosition?.hasConflict ? (
            conflictText
          ) : !oldSimplifiedUndercutMode ||
            oldSimplifiedUndercutMode ===
              SimplifiedUndercutMode.CheapestListing ||
            !oldReferencePosition ||
            oldReferencePosition <= 1 ? (
            <Content id={ContentId.TheCheapestListing} />
          ) : oldSimplifiedUndercutMode ===
            SimplifiedUndercutMode.NthCheapestListing ? (
            <FormatContent
              id={FormatContentId.TheXthCheapestListing}
              params={[
                <Content
                  key="0"
                  id={
                    UNDERCUT_REFERENCE_LISTING_POSITION_TO_CID[
                      oldReferencePosition
                    ]
                  }
                />,
              ]}
            />
          ) : oldSimplifiedUndercutMode ===
            SimplifiedUndercutMode.AvgOfNthCheapestListings ? (
            <FormatContent
              id={FormatContentId.AvgOfTheXCheapestListings}
              params={[oldReferencePosition?.toString() || '']}
            />
          ) : (
            naText
          ),
        to:
          !newSimplifiedUndercutMode ||
          newSimplifiedUndercutMode ===
            SimplifiedUndercutMode.CheapestListing ||
          !newReferencePosition ||
          newReferencePosition <= 1 ? (
            <Content id={ContentId.TheCheapestListing} />
          ) : newSimplifiedUndercutMode ===
            SimplifiedUndercutMode.NthCheapestListing ? (
            <FormatContent
              id={FormatContentId.TheXthCheapestListing}
              params={[
                <Content
                  key="0"
                  id={
                    UNDERCUT_REFERENCE_LISTING_POSITION_TO_CID[
                      newReferencePosition
                    ]
                  }
                />,
              ]}
            />
          ) : newSimplifiedUndercutMode ===
            SimplifiedUndercutMode.AvgOfNthCheapestListings ? (
            <FormatContent
              id={FormatContentId.AvgOfTheXCheapestListings}
              params={[newReferencePosition?.toString() || '']}
            />
          ) : (
            naText
          ),
        include:
          !!changedFields?.undercutMode ||
          !!changedFields?.undercutReferenceListingPosition,
        onChange: (include: boolean) => {
          updateChangedFields({
            undercutMode: include,
            undercutReferenceListingPosition: include,
          });
        },
      });
    }

    return settings;
  }, [
    updates,
    defaultValues,
    settingsInput.compListingQuantityScoreAdjustmentOverrideJson?.hasConflict,
    settingsInput.autoPricingEnabled?.hasConflict,
    settingsInput.compListingMode?.hasConflict,
    settingsInput.compListingExcludeFanInventory?.hasConflict,
    settingsInput.compListingExcludeDefects?.hasConflict,
    settingsInput.compListingSelectedSectionSettings?.hasConflict,
    settingsInput.undercutAbsoluteAmount?.hasConflict,
    settingsInput.undercutRelativeAmount?.hasConflict,
    settingsInput.netProceedsFloor?.hasConflict,
    settingsInput.netProceedsCeiling?.hasConflict,
    settingsInput.circuitBreakerMinCompListingCount?.hasConflict,
    settingsInput.outlierMode?.hasConflict,
    settingsInput.outlierStandardDeviations?.hasConflict,
    settingsInput.outlierKthLowestLimit?.hasConflict,
    settingsInput.outlierKthLowestLimitAbsoluteSpacing?.hasConflict,
    settingsInput.outlierKthLowestLimitRelativeSpacing?.hasConflict,
    settingsInput.circuitBreakerMaxDiscountVelocityPercent?.hasConflict,
    settingsInput.undercutMode?.hasConflict,
    settingsInput.undercutReferenceListingPosition?.hasConflict,
    conflictText,
    changedFields?.autoPricingEnabled,
    changedFields?.compListingMode,
    changedFields?.compListingExcludeFanInventory,
    changedFields?.compListingExcludeDefects,
    changedFields?.compListingSelectedSectionSettings,
    changedFields?.deriveCompsFromVenueZoneConfig,
    changedFields?.includeBetterZones,
    changedFields?.rowCompOffset,
    changedFields?.compListingQuantityScoreAdjustmentOverrideJson,
    changedFields?.undercutAbsoluteAmount,
    changedFields?.undercutRelativeAmount,
    changedFields?.netProceedsFloor,
    changedFields?.netProceedsCeiling,
    changedFields?.netProceedsFloorAdjustmentAmount,
    changedFields?.circuitBreakerMinCompListingCount,
    changedFields?.outlierMode,
    changedFields?.outlierStandardDeviations,
    changedFields?.outlierKthLowestLimit,
    changedFields?.outlierKthLowestLimitAbsoluteSpacing,
    changedFields?.outlierKthLowestLimitRelativeSpacing,
    changedFields?.circuitBreakerMaxDiscountVelocityPercent,
    changedFields?.circuitBreakerMaxDiscountVelocityTicksInHours,
    changedFields?.undercutMode,
    changedFields?.undercutReferenceListingPosition,
    updateChangedFields,
    naText,
    getSectionsValueText,
    useAccountDefaultText,
    sellerFee,
  ]);

  return {
    data,
    columns: PricingSettingsPreviewColumns,
  };
};
